import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../../stores/pageBuilderStore";
import bodyStylesManager from "../../../stores/body-styles-manager";
import DimensionsBox from "../shared/satori-ui/dimensions-box";
import { Layout, AlignCenter, AlignLeft, AlignRight, Link, Unlink } from "lucide-react";
import { ILayoutStyles } from "../../../stores/state-interfaces";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import { ALIGN_OPTIONS } from "../../../types/constants";

/**
 * LayoutSection renders controls for adjusting the overall layout properties of the page or email body
 * within the PageBuilder. This section is typically included as part of the SidebarPanel's "Body Properties" tab.
 *
 * Features:
 * - Select container type (Boxed or Full Width) for the overall page layout.
 * - Choose maximum content width (predefined sizes or custom).
 * - Set horizontal content alignment (left, center, right).
 * - Adjust body padding for all four sides (top, bottom, left, right).
 *
 * @component
 * @remarks
 * - Intended for internal use only as part of the body settings; not a standalone or reusable component.
 * - This component currently does not accept props and uses static/default values; application logic and state management
 *   (for value changes) will be integrated as the builder develops.
 * - The set of controls is subject to change or expand as more layout features are added.
 *
 * @example
 * // Used within the SidebarPanel's "Body Properties" section:
 * <LayoutSection />
 */
export default observer(function LayoutSection() {
  const layout = bodyStylesManager.getLayoutStyles();

  // Safe fallback for padding (put at the top of your component)
  const safePadding = layout?.padding || {
    top: "",
    right: "",
    bottom: "",
    left: "",
    unit: "px",
    linked: true,
  };

  // Handler for toggling linked/unlinked mode
  function togglePaddingLinked() {
    // Defensive: If layout or layout.padding is missing, initialize with defaults
    const currentPadding = layout?.padding || {
      top: "",
      right: "",
      bottom: "",
      left: "",
      unit: "px",
      linked: true
    };

    // Toggle the linked state
    const nextLinked = !currentPadding.linked;

    // When linking: set all sides to top value
    // When unlinking: keep sides as-is
    const nextPadding = nextLinked
      ? {
          top: currentPadding.top,
          right: currentPadding.top,
          bottom: currentPadding.top,
          left: currentPadding.top,
          unit: currentPadding.unit,
          linked: nextLinked
        }
      : {
          ...currentPadding,
          linked: nextLinked
        };

    bodyStylesManager.setLayoutStyles({
      ...(layout || {}), // Defensive: If layout is undefined, start with empty
      padding: nextPadding
    });
  }

  return (
    <SatoriCollapsiblePanel
      icon={<Layout size={18} strokeWidth={1.5} />}
      title={"Layout"}
      defaultOpen={true}
    >
      <div className="flex flex-col gap-3">
        <SatoriDropDownList
          label={"Container Width"}
          value={layout?.containerType || "full"}
          options={[
            { text: "Full Width", value: "full" },
            { text: "Boxed", value: "boxed" }
          ]}
          onChange={val => {
            bodyStylesManager.setLayoutStyles({
              ...(layout || {}),
              containerType: val === "full" ? "full" : "boxed"
            });

            if (val === "full") {
              bodyStylesManager.setLayoutStyles({
                ...(layout || {}),
                maxWidth: {
                  value: "100",
                  unit: "%"
                }
              })
            } else {
              bodyStylesManager.setLayoutStyles({
                ...(layout || {}),
                maxWidth: {
                  value: "1200",
                  unit: "px"
                }
              })
            }
          }}
          isFontFamily={false}
        />

        {/* Max Width */}
        {layout?.containerType === "boxed" &&
          <DimensionsBox
            label="Max Width"
            value={layout?.maxWidth?.value || ""}
            unit={layout?.maxWidth?.unit || "px"}
            units={["px", "%"]}
            onValueChange={val =>
              bodyStylesManager.setLayoutStyles({
                ...(layout || {}),
                maxWidth: {
                  value: val,
                  unit: layout?.maxWidth?.unit || "px"
                }
              })
            }
            onUnitChange={unit =>
              bodyStylesManager.setLayoutStyles({
                ...(layout || {}),
                maxWidth: {
                  value: layout?.maxWidth?.value || "",
                  unit // always defined from the select
                }
              })
            }
          />
        }

        {/* Background Color */}
        <SatoriColorPicker
          label={"Background Color"}
          value={layout?.backgroundColor || "#ffffff"}
          onChange={val => 
            bodyStylesManager.setLayoutStyles({
              ...(layout || {}),
              backgroundColor: val
            })
          }
        />

        {/* Alignment */}
        <SatoriDropDownList
          layout="horizontal"
          label={"Alignment"}
          value={layout?.alignment || "center"}
          options={ALIGN_OPTIONS}
          isFontFamily={false}
          onChange={val => 
            bodyStylesManager.setLayoutStyles({
                ...(layout || {}),
                alignment: val as "left" | "center" | "right",
              })
          }
        />

        {/* Padding */}
        <div>
          <div className="flex items-center gap-2 w-full mb-3 justify-between">
            <span className="text-base pt-2">Padding</span>
            <button
              onClick={togglePaddingLinked}
              className="ml-1 p-1"
              title={safePadding.linked ? "Unlink sides" : "Link all sides"}
            >
              {safePadding.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>

          {safePadding.linked ? (
            <DimensionsBox
              value={safePadding.top}
              unit={safePadding.unit}
              onValueChange={val =>
                bodyStylesManager.setLayoutStyles({
                  ...(layout || {}),
                  padding: {
                    ...safePadding,
                    top: val,
                    right: val,
                    bottom: val,
                    left: val,
                  },
                })
              }
              onUnitChange={unit =>
                bodyStylesManager.setLayoutStyles({
                  ...(layout || {}),
                  padding: { ...safePadding, unit },
                })
              }
            />
          ) : (
            <div className="flex flex-col gap-3 w-full">
              <DimensionsBox
                label="Top"
                value={safePadding.top}
                unit={safePadding.unit}
                onValueChange={val =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, top: val },
                  })
                }
                onUnitChange={unit =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, unit },
                  })
                }
              />
              <DimensionsBox
                label="Right"
                value={safePadding.right}
                unit={safePadding.unit}
                onValueChange={val =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, right: val },
                  })
                }
                onUnitChange={unit =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, unit },
                  })
                }
              />
              <DimensionsBox
                label="Bottom"
                value={safePadding.bottom}
                unit={safePadding.unit}
                onValueChange={val =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, bottom: val },
                  })
                }
                onUnitChange={unit =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, unit },
                  })
                }
              />
              <DimensionsBox
                label="Left"
                value={safePadding.left}
                unit={safePadding.unit}
                onValueChange={val =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, left: val },
                  })
                }
                onUnitChange={unit =>
                  bodyStylesManager.setLayoutStyles({
                    ...(layout || {}),
                    padding: { ...safePadding, unit },
                  })
                }
              />
            </div>
          )}
        </div>
      </div>
      {/* Add more layout fields here (margin, border radius, bg, etc.) */}
    </SatoriCollapsiblePanel>
  );
});